import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import * as Sentry from '@sentry/react';
import {getLexicalPasteErrorDetails} from 'helpers/lexicalPasteErrors';
import {
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  LexicalEditor,
  LexicalNode,
  PASTE_COMMAND,
  PasteCommandType,
  PointType,
} from 'lexical';
import {useEffect} from 'react';

type DiagnosticValue = boolean | number | string | null | DiagnosticRecord | DiagnosticValue[];
type DiagnosticRecord = {[key: string]: DiagnosticValue};
type FlatDiagnosticRecord = {[key: string]: boolean | number | string | null};

const safely = <T,>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

const describeBlockAncestor = (node: LexicalNode): DiagnosticRecord | null => {
  let current: LexicalNode | null = node;

  for (let depth = 0; current && depth < 20; depth += 1) {
    const candidate: LexicalNode = current;
    if (
      $isElementNode(candidate) &&
      candidate.getType() !== 'root' &&
      !safely(() => candidate.isInline(), false)
    ) {
      return {
        attached: safely(() => candidate.isAttached(), false),
        depth,
        key: candidate.getKey(),
        type: candidate.getType(),
      };
    }
    current = safely<LexicalNode | null>(() => candidate.getParent(), null);
  }

  return null;
};

export const describeLexicalNodeForPaste = (node: LexicalNode): DiagnosticRecord => {
  const parent = safely(() => node.getParent(), null);

  return {
    attached: safely(() => node.isAttached(), false),
    block_ancestor: describeBlockAncestor(node),
    key: node.getKey(),
    parent: parent
      ? {
          attached: safely(() => parent.isAttached(), false),
          key: parent.getKey(),
          type: parent.getType(),
        }
      : null,
    type: node.getType(),
  };
};

const describePoint = (point: PointType): DiagnosticRecord => {
  const base: DiagnosticRecord = {
    key: point.key,
    offset: point.offset,
    point_type: point.type,
  };

  try {
    return {...base, node: describeLexicalNodeForPaste(point.getNode())};
  } catch (error) {
    return {
      ...base,
      node_error: getLexicalPasteErrorDetails(error) ?? {
        name: error instanceof Error ? error.name.slice(0, 128) : 'unknown',
      },
    };
  }
};

export const collectLexicalSelectionForPaste = (): DiagnosticRecord => {
  const selection = $getSelection();

  if (selection === null) {
    return {kind: 'none'};
  }

  if ($isRangeSelection(selection)) {
    return {
      anchor: describePoint(selection.anchor),
      collapsed: safely(() => selection.isCollapsed(), false),
      focus: describePoint(selection.focus),
      kind: 'range',
    };
  }

  if ($isNodeSelection(selection)) {
    return {
      kind: 'node',
      nodes: safely(() => selection.getNodes().slice(0, 10).map(describeLexicalNodeForPaste), []),
    };
  }

  return {kind: selection.constructor.name || 'unknown'};
};

const describeDomNode = (
  node: Node | null,
  editorRoot: HTMLElement | null
): DiagnosticRecord | null =>
  node
    ? {
        inside_editor: Boolean(editorRoot && (node === editorRoot || editorRoot.contains(node))),
        node_name: node.nodeName,
        node_type: node.nodeType,
      }
    : null;

export const collectDomSelectionForPaste = (editorRoot: HTMLElement | null): DiagnosticRecord => {
  const selection = window.getSelection();
  const activeElement = document.activeElement;

  return {
    active_element_inside_editor: Boolean(
      editorRoot &&
      activeElement &&
      (activeElement === editorRoot || editorRoot.contains(activeElement))
    ),
    anchor: describeDomNode(selection?.anchorNode ?? null, editorRoot),
    anchor_offset: selection?.anchorOffset ?? null,
    collapsed: selection?.isCollapsed ?? null,
    editor_root_connected: editorRoot?.isConnected ?? false,
    focus: describeDomNode(selection?.focusNode ?? null, editorRoot),
    focus_offset: selection?.focusOffset ?? null,
    range_count: selection?.rangeCount ?? 0,
  };
};

export const collectClipboardShapeForPaste = (
  clipboardData: DataTransfer | null
): DiagnosticRecord => {
  if (!clipboardData) {
    return {available: false};
  }

  const safeTypes = new Set([
    'Files',
    'application/x-lexical-editor',
    'text/html',
    'text/plain',
    'text/rtf',
  ]);
  const rawTypes = Array.from(clipboardData.types).slice(0, 20);
  const types = rawTypes.filter((type) => safeTypes.has(type));
  const lengths: DiagnosticRecord = {};
  types.forEach((type) => {
    lengths[type.slice(0, 128)] = safely(() => clipboardData.getData(type).length, -1);
  });

  return {
    available: true,
    file_count: clipboardData.files?.length ?? 0,
    item_count: clipboardData.items?.length ?? 0,
    lengths,
    types,
    unknown_type_count: rawTypes.length - types.length,
  };
};

const createPasteAttemptId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

type PasteAttempt = {
  attemptId: string;
  source: string;
  startedAt: number;
};

const latestPasteAttempts = new WeakMap<LexicalEditor, PasteAttempt>();
const PASTE_ERROR_CORRELATION_WINDOW_MS = 10_000;

const collectLexicalSelectionAtError = (editor: LexicalEditor): DiagnosticRecord => {
  try {
    // Lexical calls onError while its pending editor state is still active.
    return collectLexicalSelectionForPaste();
  } catch {
    return safely(() => editor.getEditorState().read(() => collectLexicalSelectionForPaste()), {
      kind: 'unavailable',
    });
  }
};

/**
 * Called by RichTextArea's Lexical onError hook. This must never mask or replace
 * the original editor error.
 */
export const addPasteErrorDiagnostics = (error: Error, editor: LexicalEditor): void => {
  try {
    const attempt = latestPasteAttempts.get(editor);
    if (!attempt) {
      return;
    }

    const elapsedMs = Date.now() - attempt.startedAt;
    if (elapsedMs < 0 || elapsedMs > PASTE_ERROR_CORRELATION_WINDOW_MS) {
      latestPasteAttempts.delete(editor);
      return;
    }

    const errorDetails = getLexicalPasteErrorDetails(error);
    Sentry.addBreadcrumb({
      category: 'lexical.paste.error',
      data: flattenPasteDiagnostics({
        attempt_id: attempt.attemptId,
        dom_selection: safely(() => collectDomSelectionForPaste(editor.getRootElement()), {
          kind: 'unavailable',
        }),
        editable: safely(() => editor.isEditable(), false),
        elapsed_ms: elapsedMs,
        error: errorDetails ?? {code: 'other'},
        lexical_selection: collectLexicalSelectionAtError(editor),
        phase: 'error',
        source: attempt.source,
      }),
      level: 'error',
      message: 'Lexical paste error snapshot',
    });
  } catch {
    // Diagnostics are strictly fail-open so the original Lexical behavior wins.
  }
};

export const flattenPasteDiagnostics = (value: DiagnosticRecord): FlatDiagnosticRecord => {
  const output: FlatDiagnosticRecord = {};
  const isPrimitive = (child: DiagnosticValue): child is boolean | number | string | null =>
    child === null || ['boolean', 'number', 'string'].includes(typeof child);

  const visit = (record: DiagnosticRecord, prefix = '') => {
    Object.entries(record).forEach(([key, child]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (isPrimitive(child)) {
        output[path] = child;
      } else if (Array.isArray(child)) {
        if (child.every(isPrimitive)) {
          output[path] = child.join(',').slice(0, 1000);
        } else {
          child.slice(0, 10).forEach((item, index) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              visit(item, `${path}.${index}`);
            }
          });
        }
      } else {
        visit(child, path);
      }
    });
  };

  visit(value);

  return output;
};

export const addPasteAttemptDiagnostics = (
  editor: LexicalEditor,
  event: PasteCommandType,
  source?: string
): false => {
  try {
    const attempt: PasteAttempt = {
      attemptId: createPasteAttemptId(),
      source: source || 'unknown',
      startedAt: Date.now(),
    };
    latestPasteAttempts.set(editor, attempt);

    const clipboardData =
      typeof ClipboardEvent !== 'undefined' && event instanceof ClipboardEvent
        ? event.clipboardData
        : null;

    Sentry.addBreadcrumb({
      category: 'lexical.paste',
      data: flattenPasteDiagnostics({
        attempt_id: attempt.attemptId,
        clipboard: collectClipboardShapeForPaste(clipboardData),
        dom_selection: collectDomSelectionForPaste(editor.getRootElement()),
        editable: editor.isEditable(),
        lexical_selection: collectLexicalSelectionForPaste(),
        phase: 'before',
        source: attempt.source,
      }),
      level: 'info',
      message: 'Lexical paste selection snapshot',
    });
  } catch {
    // A diagnostic failure must never prevent Lexical's paste handler from running.
  }

  return false;
};

export const PasteDiagnosticsPlugin = ({source}: {source?: string}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregister = editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => addPasteAttemptDiagnostics(editor, event, source),
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      unregister();
      latestPasteAttempts.delete(editor);
    };
  }, [editor, source]);

  return null;
};
